import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT;

// Get authentication token from localStorage
function getAuthToken() {
  const tokens = localStorage.getItem('tokens');
  if (!tokens) return null;

  try {
    const parsed = JSON.parse(tokens);
    return parsed.idToken;
  } catch (e) {
    console.error('Failed to parse tokens:', e);
    return null;
  }
}

// Create project (handles both FormData for CSV uploads and JSON for Knowledge Base)
export const createProject = async (projectType, formData) => {
  const token = getAuthToken();

  let data;
  let headers = {
    Authorization: `Bearer ${token}`,
  };

  if (projectType === 'knowledge_base') {
    // Send as JSON
    data = JSON.stringify(formData);
    headers['Content-Type'] = 'application/json';
  } else {
    // Send as FormData for CSV uploads
    data = formData;
    headers['Content-Type'] = 'multipart/form-data';
  }

  const response = await axios.post(`${API_BASE_URL}/projects`, data, { headers });
  return response.data;
};

// List all projects (with optional filters)
export const getProjects = async (filters = {}) => {
  const token = getAuthToken();

  const params = {};
  if (filters.type) params.type = filters.type;
  if (filters.search) params.search = filters.search;

  const response = await axios.get(`${API_BASE_URL}/projects`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });

  return response.data;
};

// Get single project
export const getProject = async (projectId) => {
  const token = getAuthToken();

  const response = await axios.get(`${API_BASE_URL}/projects/${projectId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

// Delete project
export const deleteProject = async (projectId) => {
  const token = getAuthToken();

  const response = await axios.delete(`${API_BASE_URL}/projects/${projectId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

// Update project (for Knowledge Base date editing)
export const updateProject = async (projectId, updates) => {
  const token = getAuthToken();

  const response = await axios.patch(`${API_BASE_URL}/projects/${projectId}`, updates, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  return response.data;
};

// Get project CSV data
export const getProjectData = async (projectId) => {
  const token = getAuthToken();

  const response = await axios.get(`${API_BASE_URL}/projects/${projectId}/data`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

// Upload new CSV (for Data Capture/Appointment Reminders)
export const uploadNewCSV = async (projectId, file) => {
  const token = getAuthToken();

  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(`${API_BASE_URL}/projects/${projectId}/upload`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Download template
export const downloadTemplate = async (projectType) => {
  const token = getAuthToken();

  const response = await axios.get(`${API_BASE_URL}/projects/templates/${projectType}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: 'blob',
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${projectType}_template.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
